import makeApiCall from "./master/axiosIndex";

/**
 * Equipment Hourly Use Rate — EFFECTIVE-DATED.
 *
 * Identity lives in the Asset Master (assetApis); this API owns the rate
 * VERSIONS. Every save INSERTS a version with an `effectiveFrom` instant —
 * nothing is edited in place. The version in force at time t = the latest
 * effectiveFrom <= t; reads take an optional `asOf` ISO datetime ("what did
 * this machine cost on that date"). Back-dating a version recomputes history
 * BY DESIGN. Only the LATEST version can be deleted (undo).
 *
 * The shared rate-parameters card (fuel/power prices + the ten crew wages) is
 * versioned the same way: one chain per (org, project), resolution project
 * chain first then org.
 *
 * A SEPARATE entity from Machinery Norms, not one of its heads: the eight cost
 * heads (Depreciation, Interest, Repair, Misc, Insurance, Electricity, Fuel,
 * Lubricants) plus Crew are COMPUTED server-side from ~24 engineering inputs
 * and are never uploaded. Nothing here takes a `category`.
 *
 * Decimals travel as strings — parse on the consumer side.
 *
 * PERCENTAGES: the API stores fractions (0.10 = 10%), the UI works in whole
 * numbers. Convert at the form boundary, never in here.
 */

/**
 * The nine heads expressed PER UNIT OF OUTPUT (₹/Cum).
 *
 * Two different denominators produce this, and they are NOT interchangeable:
 *   - rate master      1 / standardOutputPerHour
 *   - deployment norm  AF x (1 + NPF) / (prodNorm x EUF)
 * The deployment one is normally larger (it accounts for non-production hours
 * and utilisation), so a machine's ₹/Cum on a task legitimately exceeds its
 * ₹/Cum on the fleet list.
 *
 * A head the engine could not compute is ABSENT, and the whole object is
 * absent when the grain has no denominator. Render that as an em dash — never
 * as 0, and never by falling back to the ₹/hr figure.
 *
 * Four decimal places on the wire; format with moneyPerUnit() from
 * shared/useRateUnit, which keeps sub-paisa heads visible.
 */
export interface EquipmentPerUnitHeads {
  depreciation?: string;
  interest?: string;
  insurance?: string;
  repair?: string;
  misc?: string;
  electricity?: string;
  fuel?: string;
  lubricants?: string;
  crew?: string;
  total?: string;
  /** machine-hours per one unit of output — the multiplier that was applied */
  hoursPerUnit?: string;
  /** denominator label, e.g. "Cum"; blank when the grain mixes units */
  uomName?: string;
  uomId?: number;
}

/** The nine computed heads, as returned alongside every rated asset. */
export interface EquipmentRateHeads {
  annualHours: string | null;
  depreciation: string | null;
  interest: string | null;
  insurance: string | null;
  repair: string | null;
  misc: string | null;
  electricity: string | null;
  fuel: string | null;
  lubricants: string | null;
  crew: string | null;
  total: string | null;
  /** MISSING_LIFE_DATA, RATE_PARAMETERS_MISSING, WAGE_MISSING:<ROLE>, ... */
  warnings?: string[];
  calcVersion?: string;
  /** PROJECT | ORG | NONE — which rate-parameters card produced these numbers. */
  resolvedFrom?: string;
  /**
   * ₹/unit projection of the heads above. Rides inside `heads`, so the fleet
   * list, getAssetRate() and preview() all carry it. Absent when the version
   * records no standardOutputPerHour.
   */
  perUnit?: EquipmentPerUnitHeads;
}

/**
 * The workbook's per-section derived rows (annual hours live on the heads).
 * Returned by preview() and getAssetRate() only — never on the list. Lacs
 * fields are in lakhs, matching the source sheet; monthly crew figures are in
 * rupees.
 */
export interface EquipmentRateIntermediates {
  insuranceCostPerYearLacs: string | null;
  averageAnnualCostLacs: string | null;
  interestCostPerYearLacs: string | null;
  dieselLitresPerHour: string | null;
  effectiveDieselLitresPerHour: string | null;
  dieselPriceUsed: string | null;
  dieselLubricants: string | null;
  fuelAndLubSubtotal: string | null;
  kwhPerHour: string | null;
  electricityRateUsed: string | null;
  elecLubricants: string | null;
  powerAndLubSubtotal: string | null;
  /** fuel + electricity + lubricants + misc — the workbook's Total POL row. */
  totalPol: string | null;
  skilledMonthly: string | null;
  unskilledMonthly: string | null;
  skilledHourly: string | null;
  unskilledHourly: string | null;
}

/** One row of the section-6 Manpower table: role, wage in force, requirement, cost. */
export interface CrewBreakdownLine {
  key: string;
  label: string;
  skill: string;
  nosPerShift: string;
  monthlyWage: string;
  monthlyCost: string;
  hourly: string;
}

/** The ~24 engineering inputs of one version. Numbers as the user typed them. */
export interface EquipmentRateFields {
  costOfEquipmentLacs?: number;
  scrapValuePct?: number;
  lifeHours?: number;
  lifeYears?: number;

  /** Output units per hour — a PLAIN rate, not a percentage. */
  standardOutputPerHour?: number;
  /** uom_master.id. outputUomName comes back on reads, for the label. */
  outputUomId?: number;

  insurancePctPerYear?: number;
  interestRatePct?: number;
  repairPctOfCost?: number;
  sundriesPctOfRepair?: number;

  bhpRating?: number;
  dieselC1?: number;
  dieselC2?: number;
  dieselEffectiveUsagePct?: number;
  lubricantPctOfFuel?: number;

  totalPowerKw?: number;
  elecC1?: number;
  elecC2?: number;
  elecEffectiveUsagePct?: number;
  lubricantPctOfElectricity?: number;

  noOfShifts?: number;
  indirectSkilledPct?: number;
  indirectUnskilledPct?: number;

  crewForemanNos?: number;
  crewOperatorNos?: number;
  crewMechanicNos?: number;
  crewElectricianNos?: number;
  crewSupervisorNos?: number;
  crewDriverNos?: number;
  crewCablemanNos?: number;
  crewHelperNos?: number;
  crewWatchmanNos?: number;
  crewBeldarNos?: number;
}

/** A save: inputs + WHEN they take effect + why the rate changed. */
export interface SaveAssetRateVersionPayload extends EquipmentRateFields {
  assetId: number;
  /** ISO datetime. May be in the past — back-dating recomputes history. */
  effectiveFrom: string;
  remarks?: string;
}

export const equipmentRateApis = {
  /** INSERT a new rate version for an asset. */
  saveVersion: (data: SaveAssetRateVersionPayload): Promise<any> =>
    makeApiCall({ url: "/machinery-equipment/version", method: "post", data }),

  /** Undo: removes the asset's LATEST version only; history is immutable. */
  deleteLatestVersion: (assetId: number): Promise<any> =>
    makeApiCall({ url: `/machinery-equipment/version/${assetId}`, method: "delete" }),

  /**
   * The fleet: paginated ASSETS (identity from the Asset Master), each carrying
   * the rate in force at `asOf` (default now), its computed heads and version
   * count. `asOf` is what powers the "cost on any date" picker.
   */
  getList: (params: any): Promise<any> =>
    makeApiCall({ url: "/machinery-equipment/list", method: "get", params }),

  /** One asset's rate as-of: version + heads + intermediates + crew table. */
  getAssetRate: (assetId: number, asOf?: string): Promise<any> =>
    makeApiCall({
      url: `/machinery-equipment/asset/${assetId}`,
      method: "get",
      params: asOf ? { asOf } : undefined,
    }),

  /** Full version timeline, newest first, each priced as-of its own instant. */
  getHistory: (assetId: number): Promise<any> =>
    makeApiCall({ url: `/machinery-equipment/history/${assetId}`, method: "get" }),

  /**
   * Rate an UNSAVED draft against the card as-of `asOf`. The editor's computed
   * panel calls this rather than re-implementing the nine-head chain in
   * TypeScript — one formula, one place. POST because the body is the whole form.
   */
  preview: (
    data: EquipmentRateFields & { projectId?: number; asOf?: string },
  ): Promise<any> =>
    makeApiCall({ url: "/machinery-equipment/preview", method: "post", data }),

  /**
   * Excel: rows are matched to EXISTING assets by code then name; each changed
   * row inserts a version effective from the commit instant. Unknown machine =
   * row error pointing at the Asset Master.
   */
  uploadExcel: (data: FormData): Promise<any> =>
    makeApiCall({ url: "/machinery-equipment/upload/excel", method: "post", data }),

  /**
   * Async export: one row per asset with the version in force now. Enqueues a
   * build job (a worker builds the xlsx and stores it in DMS). Returns the
   * request reportId; poll fetchExportList for filePath.
   */
  requestExport: (comment?: string, projectId?: number): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/export/request",
      method: "post",
      data: { comment, projectId },
    }),

  fetchExportList: (): Promise<any> =>
    makeApiCall({ url: "/machinery-equipment/export/list", method: "get" }),
};

export interface SaveRateParameterVersionPayload {
  /** 0/absent = the org card. */
  projectId?: number;
  /** ISO datetime this card takes effect. */
  effectiveFrom: string;

  dieselPricePerLitre?: number;
  electricityRatePerKwh?: number;

  wageForeman?: number;
  wageOperator?: number;
  wageMechanic?: number;
  wageElectrician?: number;
  wageSupervisor?: number;
  wageDriver?: number;
  wageCableman?: number;
  wageHelper?: number;
  wageWatchman?: number;
  wageBeldar?: number;

  defaultScrapValuePct?: number;
  defaultInterestRatePct?: number;
  defaultSundriesPctOfRepair?: number;

  remarks?: string;
}

export const rateParameterApis = {
  /** INSERT a new card version for the scope. One edit re-rates the fleet from its date. */
  saveVersion: (data: SaveRateParameterVersionPayload): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/rate-parameters/version",
      method: "post",
      data,
    }),

  /** Undo: removes the scope's LATEST card version only. */
  deleteLatestVersion: (projectId: number, rateParameterId: number): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/rate-parameters/version",
      method: "delete",
      data: { projectId, rateParameterId },
    }),

  /** The card in force at `asOf` (default now): project chain first, org fallback. */
  fetch: (projectId = 0, asOf?: string): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/rate-parameters",
      method: "get",
      params: { projectId, ...(asOf ? { asOf } : {}) },
    }),

  /** The scope's own card timeline (no fallback), newest first. */
  fetchHistory: (projectId = 0): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/rate-parameters/history",
      method: "get",
      params: { projectId },
    }),
};

// ---------------------------------------------------------------------------
// Machinery Deployment Norms — versioned, effective-dated, WHOLE-MATRIX grain.
//
// A norm = one BoQ Norm Code (B3.1.1…) owning a MATRIX of equipment lines,
// each with three fractional factors (NPF/EUF/AF), a production-norm unit and
// a Prod. Norm. Every save appends a COMPLETE matrix as a new version with an
// `effectiveFrom` instant — the version in force at time t is the latest
// effectiveFrom <= t; back-dating recomputes history BY DESIGN; only the
// latest version is deletable (undo). Total Working HRS is DERIVED, never
// stored: hours = (qty × AF) / (prodNorm × EUF) × (1 + NPF).
//
// Factors travel as FRACTIONS on the wire (0.05 = 5%), decimals as strings.
// Scope = (org, projectId); projectId 0 = organization master.

/** One equipment line of a version, as SENT. Factors are fractions. */
export interface DeploymentNormLinePayload {
  assetId: number;
  nonProductionHoursFactor?: number;
  effectiveUtilizationFactor?: number;
  applicationFactor?: number;
  uomId?: number;
  prodNorm?: number;
}

/** A save: the FULL matrix + when it takes effect + why. */
export interface SaveDeploymentNormVersionPayload {
  /** Absent = create a new header (by projectId + normCode). */
  deploymentNormId?: number;
  /** 0/absent = the org master. */
  projectId?: number;
  normCode: string;
  description?: string;
  /** ISO datetime. May be in the past — back-dating recomputes history. */
  effectiveFrom: string;
  remarks?: string;
  /** The lines array IS the matrix — a version always carries all of it. */
  lines: DeploymentNormLinePayload[];
}

export const deploymentNormApis = {
  /** Create header if new + APPEND a full-matrix version. */
  saveVersion: (data: SaveDeploymentNormVersionPayload): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/version",
      method: "post",
      data,
    }),

  /** Header-only edit (normCode rename / description). Versions untouched. */
  updateHeader: (data: {
    deploymentNormId: number;
    normCode?: string;
    description?: string;
  }): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/update",
      method: "post",
      data,
    }),

  /** Undo: removes the norm's LATEST version only; history is immutable. */
  deleteLatestVersion: (deploymentNormId: number): Promise<any> =>
    makeApiCall({
      url: `/machinery-equipment/deployment-norms/version/${deploymentNormId}`,
      method: "delete",
    }),

  deactivate: (deploymentNormId: number): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/deactivate",
      method: "post",
      data: { deploymentNormId },
    }),

  activate: (deploymentNormId: number): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/activate",
      method: "post",
      data: { deploymentNormId },
    }),

  /**
   * Keyset-paginated headers, each with the version-in-force summary at
   * `asOf` (default now): effectiveFrom | null, equipmentCount, deployedCount
   * (prodNorm > 0), versionCount.
   */
  getList: (params: any): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/list",
      method: "get",
      params,
    }),

  /** Header + the version in force at `asOf` with its full asset-joined matrix. */
  getOne: (deploymentNormId: number, asOf?: string): Promise<any> =>
    makeApiCall({
      url: `/machinery-equipment/deployment-norms/${deploymentNormId}`,
      method: "get",
      params: asOf ? { asOf } : undefined,
    }),

  /** Version timeline, newest first, each with its embedded matrix. */
  getHistory: (deploymentNormId: number): Promise<any> =>
    makeApiCall({
      url: `/machinery-equipment/deployment-norms/history/${deploymentNormId}`,
      method: "get",
    }),

  /**
   * Excel: per-norm-code versioning — a code's rows are a candidate new
   * version effective from the COMMIT instant; identical matrix = No Change;
   * codes absent from the file are untouched.
   */
  uploadExcel: (data: FormData): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/upload/excel",
      method: "post",
      data,
    }),

  /** Async export of every in-force matrix for the scope, in template format. */
  requestExport: (comment?: string, projectId?: number): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/export/request",
      method: "post",
      data: { comment, projectId },
    }),

  fetchExportList: (): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/export/list",
      method: "get",
    }),

  // --- Task application (apply a norm's equipment crew to a task) -----------
  //
  // Only the QUANTITY is frozen at apply time (round2(overrideQty ?? task work
  // quantity); 0 valid, negatives 400). Equipment lines, hours and amounts
  // derive LIVE at read time from the version-in-force + the equipment hourly
  // rates — re-norming or re-pricing reaches applied tasks automatically.
  // Applicability = the task's OWN project only (org-master norms must be
  // duplicated into the project first).

  /** Apply norms to a task. Per-norm skips come back in `skipped[]`. */
  applyToTask: (data: {
    taskId: number;
    deploymentNormIds: number[];
    /** Replaces the task work quantity as the frozen basis; 0 allowed. */
    overrideQty?: number;
  }): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/task/apply",
      method: "post",
      data,
    }),

  /**
   * Excel bulk-apply (staged): rows are {TaskID, Norm Code, OverrideQty}.
   * FormData carries body=JSON {comment?, projectId} + the file; returns
   * {id: reportId} for the generic upload poll/commit/discard endpoints.
   * Per-row resilient — valid rows apply, invalid rows are reported. Same
   * freeze rule as applyToTask: only the quantity freezes at commit
   * (blank OverrideQty = task work quantity, 0 valid); hours/amounts stay
   * live-derived from the version-in-force + equipment rates.
   */
  uploadApplyExcel: (data: FormData): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/upload/apply-excel",
      method: "post",
      data,
    }),

  /** Soft-remove one applied norm from a task. Reason is mandatory. */
  removeFromTask: (data: {
    taskId: number;
    deploymentNormId: number;
    reason: string;
  }): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/task/remove",
      method: "post",
      data,
    }),

  /**
   * The task's applied norms, each expanded LIVE: version-in-force lines with
   * derived hours / rate / amount (+ noVersion / missingRate warnings), plus
   * grand totals. Decimals as strings; optional strings may arrive ''.
   */
  getTaskNorms: (taskId: number): Promise<any> =>
    makeApiCall({
      url: `/machinery-equipment/deployment-norms/task/${taskId}`,
      method: "get",
    }),

  /**
   * Norms applicable to a task (Active, task's own project) with the in-force
   * lines carrying factors/prodNorm so the UI can preview hours client-side.
   */
  getApplicable: (taskId: number): Promise<any> =>
    makeApiCall({
      url: `/machinery-equipment/deployment-norms/task/${taskId}/applicable`,
      method: "get",
    }),

  /** Project-wide rollup of applied deployment norms (live-derived). */
  getProjectRollup: (projectId: number): Promise<any> =>
    makeApiCall({
      url: `/machinery-equipment/deployment-norms/rollup/project/${projectId}`,
      method: "get",
    }),

  /**
   * Duplicate Active org-master norms into a real project (header + ONE
   * version seeded from the matrix in force). Empty ids = all org-master.
   */
  duplicateToProject: (data: {
    projectId: number;
    deploymentNormIds?: number[];
  }): Promise<any> =>
    makeApiCall({
      url: "/machinery-equipment/deployment-norms/duplicate",
      method: "post",
      data,
    }),
};

export default equipmentRateApis;
